import { useEffect, useRef } from 'react';
import { useGallerySelection } from '@/context/GallerySelectionContext';
import { useBrowserStore } from '@/hooks/data/browser/useBrowserStore';
import { isSelectableAsset } from '@/lib/utils';

/**
 * Reconciles the gallery selection with the assets currently shown on the page.
 *
 * When an asset is genuinely removed from the visible list (e.g. unshared, deleted, or
 * filtered out) it is dropped from the selection. Page navigation must NEVER strip
 * cross-page selections, so the hook distinguishes the two cases by id overlap:
 * a navigation swaps the whole list (no ids in common) and is ignored, while an in-place
 * removal still shares ids with the previous render.
 *
 * Overlap — not pagination counters — is the reliable signal: the assets query uses
 * `placeholderData: keepPreviousData`, so `visibleItems` lags pageIndex by a render during
 * navigation (the old page's assets are shown briefly under the new pageIndex). Keying on
 * pagination would stamp the new page onto those stale ids and wipe the selection.
 * Background refetches that return the same ids share full overlap with nothing removed,
 * so they are no-ops.
 *
 * Rows a live broadcast has taken out are the second case handled here. By design those are
 * not removed from the list - they hold their position and are marked instead - so the
 * removal test above never sees them. They are still unselectable and the row has gone
 * inert, so a selection holding one would leave the toolbar offering actions on something
 * the viewer can no longer point at.
 */
export const useReconcileSelectionWithPage = (
  visibleItems: ReadonlyArray<{ id: string; departed?: string; deleted?: boolean }> | undefined,
) => {
  const { selection, setSelectedAssets } = useGallerySelection();
  const isAllSelected = useBrowserStore((s) => s.isAllSelected);
  const prevIdsRef = useRef<Set<string> | null>(null);

  useEffect(() => {
    // A selected child folder/album is on no asset page — paging must never drop it.
    if (!visibleItems || isAllSelected || selection.kind === 'nodes') {
      return;
    }
    const currentIds = new Set(visibleItems.map((item) => item.id));
    const prevIds = prevIdsRef.current;
    prevIdsRef.current = currentIds;

    // Marked, not removed. Needs no comparison with the previous render, so it runs outside
    // the overlap guard below - a row that arrives already departed is just as unselectable.
    const drop = new Set(visibleItems.filter((item) => !isSelectableAsset(item)).map((item) => item.id));

    // First load, or a page navigation (disjoint lists): never strip cross-page selections.
    const overlaps = prevIds !== null && Array.from(currentIds).some((id) => prevIds.has(id));
    if (overlaps && prevIds !== null) {
      // Same page, list changed in place: drop items genuinely removed as well.
      Array.from(prevIds).forEach((id) => {
        if (!currentIds.has(id)) {
          drop.add(id);
        }
      });
    }

    if (drop.size === 0) {
      return;
    }
    setSelectedAssets((prev) => {
      const next = prev.filter((a) => !drop.has(a.id));
      return next.length === prev.length ? prev : next;
    });
  }, [visibleItems, setSelectedAssets, isAllSelected, selection.kind]);
};
